import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import { BadgeAvatar, AutoTooltip } from '@/components';
import { formatStreamUsd } from '@/utils';
import dayjs from 'dayjs';

import { useBadgeDetail, useBadgeHasMinted, useIsOfficialBadge } from '../../hooks';
import { useBadgeMintedCount } from '../../state';
import { MintBadgeButton } from '../MintBadgeButton';
import style from './index.module.scss';
import { OfficialBadge } from '../../types';
import { OfficialTag } from '../OfficialTag';

export const BadgeDetailHeader = ({ id }: { id: string }) => {
  const { t } = useTranslation();

  const { data, isLoading } = useBadgeDetail(id);
  const { data: mintInfo } = useBadgeHasMinted(id);
  const [badgeCount] = useBadgeMintedCount();
  const isOfficialBadge = useIsOfficialBadge();
  const fields = useMemo(
    () => [
      {
        label: t('BadgeDetailHeader.nthpai'),
        value: data?.mint_time
          ? `${dayjs.unix(data?.mint_time).utc().format('YYYY/MM/DD')} (UTC 0)`
          : '-',
      },
      { label: t('BadgeDetailHeader.1uvk6lu'), value: formatStreamUsd(data?.tvo) },
      { label: t('BadgeDetailHeader.1epf82v'), value: formatStreamUsd(data?.vo_median) },
      { label: t('BadgeDetailHeader.e6s58s'), value: formatStreamUsd(data?.vo_mean) },
    ],
    [data?.mint_time, data?.tvo, data?.vo_mean, data?.vo_median, t],
  );

  return (
    <div className={style.container}>
      <div className={style.main}>
        <div className={style.left}>
          <div className={style.leftLogo}>
            {isLoading ? (
              <Skeleton width={120} height={140} className={style.badgeLogo} />
            ) : (
              <BadgeAvatar src={data?.logo_url} className={style.badgeLogo} width={140} />
            )}
            <div className={style.mintedDesc}>
              {t('BadgeDetailHeader.1247zq9')} {badgeCount || data?._total_supply}
            </div>
          </div>
          <div className={style.badgeInfo}>
            <div className={style.badgeName}>
              <AutoTooltip title={data?.name}>{data?.name}</AutoTooltip>
              {isOfficialBadge && data && (
                <OfficialTag
                  officialId={(data as OfficialBadge)?.official_id}
                  className={style.officialTag}
                />
              )}
            </div>

            <div className={style.badgeDesc}>{data?.desc}</div>
            {data ? (
              <table className={style.fields}>
                <tbody>
                  {fields.map(x => (
                    <tr key={x.label} className={style.field}>
                      <td className={style.label}>{x.label}</td>
                      <td className={style.value}>{x.value}</td>
                      <td className={style.space} />
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        </div>
        <div className={style.right}>
          <MintBadgeButton id={id} badgeLogo={data?.logo_url} mintTime={data?.mint_time} />
          {mintInfo?.has_minted && mintInfo?.inner_id ? (
            <div className={style.mintInnerId}>#{mintInfo.inner_id}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
